import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  getPushStatus,
  sendTestPush,
  subscribePush,
  unsubscribePush,
} from "@/lib/push";

export default function PushSettings() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const s = await getPushStatus();
      setStatus(s);
    } catch {
      setStatus({ supported: false });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (!status) return null;

  if (!status.supported) {
    return (
      <div className="flex items-center justify-between gap-3" data-testid="push-settings-unsupported">
        <div>
          <p className="text-sm font-medium">Push notifications</p>
          <p className="text-xs text-muted-foreground">Not supported in this browser.</p>
        </div>
      </div>
    );
  }

  const toggle = async (on) => {
    setBusy(true);
    try {
      if (on) {
        await subscribePush();
        toast.success("Push notifications enabled");
      } else {
        await unsubscribePush();
        toast.success("Push notifications disabled");
      }
      await refresh();
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      toast.success("Test sent — check notification center");
    } catch {
      toast.error("Test failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="push-settings">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium inline-flex items-center gap-2">
            <Bell className="h-4 w-4" /> Push notifications
          </p>
          <p className="text-xs text-muted-foreground">
            Get notified when AI generations finish, OCR completes, or reminders fire.
          </p>
        </div>
        <Switch
          checked={!!status.subscribed}
          onCheckedChange={toggle}
          disabled={busy}
          data-testid="push-toggle"
        />
      </div>
      {status.subscribed && (
        <Button size="sm" variant="outline" className="rounded-full" onClick={sendTest} disabled={busy} data-testid="push-test-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Bell className="h-3.5 w-3.5 mr-1.5" />}
          Send test notification
        </Button>
      )}
    </div>
  );
}
